import { useState } from "react";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { useTranslation } from "i18n";

import { ReactComponent as Curacao } from "assets/images/cw.svg";
import { ReactComponent as NetherLands } from "assets/images/nl.svg";
import { ReactComponent as Suriname } from "assets/images/sr.svg";
import { PHONE_NUMBERS } from "constants/phoneNumbers";

const WHATSAPP_GREEN = "#25D366";
const WHATSAPP_DEEP = "#128C7E";

const formatPhone = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/[^\d]/g, "");
  if (raw.startsWith("+")) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits
      .slice(5)
      .replace(/(\d{3})(?=\d)/g, "$1 ")}`.trim();
  }
  return raw;
};

const OFFICES = [
  {
    key: "SURINAME",
    countryKey: "helpDesk.offices.suriname.country",
    locationKey: "helpDesk.offices.suriname.location",
    Flag: Suriname,
  },
  {
    key: "NETHERLANDS",
    countryKey: "helpDesk.offices.netherlands.country",
    locationKey: "helpDesk.offices.netherlands.location",
    Flag: NetherLands,
  },
  {
    key: "CURACAO",
    countryKey: "helpDesk.offices.curacao.country",
    locationKey: "helpDesk.offices.curacao.location",
    Flag: Curacao,
  },
];

const HelpDesk = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const launcherStyle = {
    position: "fixed",
    bottom: "32px",
    right: "32px",
    border: "0.5px rgb(229, 229, 229)",
    borderRadius: "10px",
    flexDirection: "column",
    bgcolor: "#fff",
    display: "flex",
    zIndex: 1000,
    boxShadow:
      "rgba(37, 42, 49, 0.16) 0px 0px 2px 0px, rgba(37, 42, 49, 0.12) 0px 1px 4px 0px",
  };

  const handleOpenWhatsApp = (country) => {
    const isMobile = window.innerWidth <= 768;
    const phoneNumber = PHONE_NUMBERS[country];
    window.open(
      isMobile
        ? `https://api.whatsapp.com/send?phone=${phoneNumber}`
        : `https://web.whatsapp.com/send?phone=${phoneNumber}`,
    );
  };

  return (
    <>
      <IconButton
        className="helpdesk-launcher"
        sx={launcherStyle}
        onClick={() => setOpen(true)}
      >
        <WhatsAppIcon sx={{ color: WHATSAPP_GREEN }} />
        <Typography fontSize="11px">{t("helpDesk.launcher")}</Typography>
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "94%", sm: 460 },
            maxWidth: "94vw",
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow:
              "0 30px 60px -15px rgba(15, 23, 42, 0.35), 0 8px 24px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${WHATSAPP_DEEP} 0%, ${WHATSAPP_GREEN} 100%)`,
              color: "#fff",
              px: { xs: 3, sm: 4 },
              pt: { xs: 4, sm: 4.5 },
              pb: 7,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 220,
                height: 220,
                borderRadius: "50%",
                backgroundColor: alpha("#ffffff", 0.08),
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -40,
                left: -30,
                width: 160,
                height: 160,
                borderRadius: "50%",
                backgroundColor: alpha("#ffffff", 0.06),
              }}
            />

            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: "#fff",
                backgroundColor: alpha("#ffffff", 0.12),
                "&:hover": { backgroundColor: alpha("#ffffff", 0.22) },
              }}
              size="small"
              aria-label={t("common.close")}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>

            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha("#ffffff", 0.18),
                  backdropFilter: "blur(6px)",
                }}
              >
                <WhatsAppIcon sx={{ color: "#fff", fontSize: 32 }} />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: "0.18em",
                    opacity: 0.85,
                    lineHeight: 1.2,
                  }}
                >
                  {t("helpDesk.support")}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, lineHeight: 1.15, mt: 0.25 }}
                >
                  {t("helpDesk.title")}
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="body2"
              sx={{ mt: 1.75, opacity: 0.92, maxWidth: 380 }}
            >
              {t("helpDesk.description")}
            </Typography>
          </Box>

          {/* Status pill */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translate(-50%, -50%)",
              top: { xs: 178, sm: 192 },
              backgroundColor: "#fff",
              borderRadius: "999px",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
              px: 2,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: `1px solid ${alpha(WHATSAPP_GREEN, 0.2)}`,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: WHATSAPP_GREEN,
                boxShadow: `0 0 0 4px ${alpha(WHATSAPP_GREEN, 0.18)}`,
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: "#0f172a" }}
            >
              {t("helpDesk.status")}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2.5, sm: 3 }, pt: 4.5 }}>
            <Stack spacing={1.25}>
              {OFFICES.map(({ key, countryKey, locationKey, Flag }) => (
                <OfficeRow
                  key={key}
                  country={t(countryKey)}
                  location={t(locationKey)}
                  phone={PHONE_NUMBERS[key]}
                  Flag={Flag}
                  onClick={() => handleOpenWhatsApp(key)}
                  chatLabel={t("helpDesk.chat")}
                />
              ))}
            </Stack>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "#64748b",
                mt: 2.5,
              }}
            >
              {t("helpDesk.redirectNotice")}
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

const OfficeRow = ({ country, location, phone, Flag, onClick, chatLabel }) => {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 1.5,
        pr: 1,
        borderRadius: "16px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        cursor: "pointer",
        transition:
          "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          borderColor: alpha(WHATSAPP_GREEN, 0.45),
          boxShadow: `0 12px 28px ${alpha(WHATSAPP_GREEN, 0.18)}`,
        },
        "&:focus-visible": {
          outline: "none",
          borderColor: WHATSAPP_GREEN,
          boxShadow: `0 0 0 4px ${alpha(WHATSAPP_GREEN, 0.2)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8fafc",
          border: "1px solid rgba(15, 23, 42, 0.06)",
          "& svg": { width: "100%", height: "100%" },
        }}
      >
        <Flag />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
          {country}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#64748b", display: "block", lineHeight: 1.3 }}
        >
          {location}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: WHATSAPP_DEEP,
            fontWeight: 700,
            display: "block",
            mt: 0.25,
          }}
        >
          {formatPhone(phone)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          backgroundColor: WHATSAPP_GREEN,
          color: "#fff",
          borderRadius: "999px",
          px: 1.75,
          py: 0.85,
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 0.2,
          flexShrink: 0,
          transition: "background-color 0.18s ease",
          "&:hover": { backgroundColor: WHATSAPP_DEEP },
        }}
      >
        {chatLabel}
        <NorthEastRoundedIcon sx={{ fontSize: 14 }} />
      </Box>
    </Box>
  );
};

export default HelpDesk;
